import toast from 'react-hot-toast';
import type { QueryClient } from '@tanstack/react-query';
import { getQueuedSales, updateOfflineSaleStatus, getPendingSaleCount, getFailedSaleCount } from '@/lib/offline-db';
import api from '@/lib/api';
import { useAuthStore } from '@/stores/auth.store';
import { getSessionEpoch } from '@/lib/session-boundary';

export interface SyncState {
  pending: number;
  syncing: boolean;
  lastSyncedCount: number;
  failedCount: number;
}

/**
 * The ONE offline-sale sync engine (POS-I64).
 *
 * It used to live inside a hook that two components mounted (the app shell and the POS page), so
 * every reconnect ran two sync loops at once and each queued sale was posted twice in parallel
 * (one of the two requests could fail with a 500 and failure toasts appeared twice). The engine is
 * now a module singleton: a single in-flight run at a time, one shared state that any component
 * reads, and exactly one place that triggers a run.
 *
 * - Sequential: one sale at a time, idempotent through `offlineId`.
 * - Error classification: network / 5xx keep the sale pending; 409 = already on the server =
 *   synced; any other 4xx is a permanent refusal (status `failed`, reason kept for the sync panel).
 */
let state: SyncState = { pending: 0, syncing: false, lastSyncedCount: 0, failedCount: 0 };
const listeners = new Set<() => void>();
let inFlight: Promise<void> | null = null;

function setState(patch: Partial<SyncState>) {
  state = { ...state, ...patch };
  listeners.forEach((l) => l());
}

export function subscribeSyncState(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getSyncState(): SyncState {
  return state;
}

/** Re-read the queue counts: sales waiting to be sent and sales the server refused. */
export async function refreshPendingCount() {
  const [pending, failedCount] = await Promise.all([getPendingSaleCount(), getFailedSaleCount()]);
  if (pending !== state.pending || failedCount !== state.failedCount) setState({ pending, failedCount });
}

interface RunOptions {
  includeFailed?: boolean;
  /** Only these queued sales (the sync panel's per-sale Retry). */
  onlyIds?: string[];
  queryClient?: QueryClient;
}

/** Start a sync run unless one is already running (then the running one is returned). */
export function runOfflineSync(opts: RunOptions = {}): Promise<void> {
  if (inFlight) return inFlight;
  inFlight = doSync(opts).finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function doSync({ includeFailed = false, onlyIds, queryClient }: RunOptions) {
  // POS-I109: a run belongs to the session that started it; it stops as soon as another user /
  // store signs in, so one owner's queued sales are never posted with the next owner's token.
  const sessionEpoch = getSessionEpoch();
  const queued = await getQueuedSales();
  const wanted = onlyIds ? queued.filter((s) => onlyIds.includes(s.id)) : queued;
  const toSync = includeFailed ? wanted : wanted.filter((s) => s.syncStatus !== 'failed');
  if (toSync.length === 0) return;

  setState({ syncing: true, pending: toSync.filter((s) => s.syncStatus !== 'failed').length });

  // Refresh JWT before syncing to avoid 401 on stale token
  try {
    await useAuthStore.getState().refreshPermissions();
  } catch {
    // Ignore — sync will try anyway; interceptor handles 401
  }

  let successCount = 0;
  let failCount = 0;

  for (const sale of toSync) {
    if (getSessionEpoch() !== sessionEpoch) break;
    await updateOfflineSaleStatus(sale.id, 'syncing');
    try {
      if (getSessionEpoch() !== sessionEpoch) break;
      const res = await api.post('/sales', sale.payload);
      const realSaleId = res.data?.id;
      await updateOfflineSaleStatus(sale.id, 'synced', { syncedSaleId: realSaleId, syncError: '' });
      successCount++;
    } catch (err: unknown) {
      // Session changed mid-request: leave the sale to its owner's next run (offlineId makes a
      // resend idempotent) and write nothing into the new owner's database.
      if (getSessionEpoch() !== sessionEpoch) break;
      const e = err as {
        response?: { status?: number; data?: { existingId?: string; message?: string | string[] } };
        message?: string;
      };
      const status = e.response?.status;
      if (status === 409) {
        // Already synced (duplicate offlineId) — treat as success
        await updateOfflineSaleStatus(sale.id, 'synced', {
          syncedSaleId: e.response?.data?.existingId,
          syncError: '',
        });
        successCount++;
      } else if (status && status >= 400 && status < 500) {
        // Permanent refusal — client-side error, do not retry automatically
        const raw = e.response?.data?.message;
        const msg = (Array.isArray(raw) ? raw.join('; ') : raw) ?? `HTTP ${status}`;
        await updateOfflineSaleStatus(sale.id, 'failed', {
          syncError: msg,
          attemptCount: (sale.attemptCount ?? 0) + 1,
        });
        failCount++;
      } else {
        // Network error or 5xx — retry next time
        await updateOfflineSaleStatus(sale.id, 'pending', {
          syncError: e.message ?? 'Network error',
          attemptCount: (sale.attemptCount ?? 0) + 1,
        });
      }
    }
  }

  if (getSessionEpoch() !== sessionEpoch) {
    setState({ syncing: false, lastSyncedCount: 0 });
    void refreshPendingCount();
    return;
  }

  const [remaining, failedInQueue] = await Promise.all([getPendingSaleCount(), getFailedSaleCount()]);
  setState({ syncing: false, pending: remaining, failedCount: failedInQueue, lastSyncedCount: successCount });

  if (successCount > 0) {
    toast.success(`${successCount} offline sale${successCount > 1 ? 's' : ''} synced`);
    queryClient?.invalidateQueries({ queryKey: ['sales'] });
    queryClient?.invalidateQueries({ queryKey: ['products-pos'] });
    queryClient?.invalidateQueries({ queryKey: ['inventory-products'] });
    queryClient?.invalidateQueries({ queryKey: ['tenant-dashboard'] });
    queryClient?.invalidateQueries({ queryKey: ['products'] });
  }
  if (failCount > 0) {
    toast.error(`${failCount} sale${failCount > 1 ? 's' : ''} failed to sync — check sync panel for details`);
  }
}
